// CT-8593: the widget must only render when embedded inside an iframe --
// never when its own URL is opened directly, forwarded, or redirected to
// as a top-level page.
//
// This checks ONLY whether the page is framed at all, not which site is
// doing the framing -- it does not restrict embedding to any particular
// domain. A per-widget allowed-domain check was deliberately left out of
// scope for CT-8593 (no such data exists yet; adding it is a separate,
// larger change).
//
// window.top / window.self are "unforgeable" browser properties (a page
// cannot redefine them, even for itself -- see the WHATWG HTML spec's
// [Unforgeable] attribute on Window), so this check cannot be spoofed by
// script running inside the widget's own document.
//
// A cross-origin parent can make window.top throw in some browser/COOP
// configurations -- that throw only happens when the page IS embedded
// cross-origin, so it's treated as confirmation of being framed, not a
// failure.
//
// Only enforced in production builds. `react-scripts start` (the `npm
// start` flow in README.md, and the Docker Compose flow in Dockerfile,
// which also runs `npm start`) always runs as a top-level page with no
// iframe -- that's the documented way to run/QA this app locally, and it
// must keep working. `react-scripts build` sets NODE_ENV to 'production'
// at build time, so every deployed environment (Dev/Test/Demo/Staging/Prod
// -- all built via `npm run build`, per azure-pipelines/) has the guard
// fully active; only a local dev-server session bypasses it.

// getSelf/getTop are injectable (defaulting to the real window accessors
// below) purely so tests can exercise the production branch without
// touching the real window.top -- jsdom implements it as non-configurable,
// matching real browsers, so tests can't override it via
// Object.defineProperty. Reading through a function (not just passing the
// values) also keeps the throwing property access inside the try/catch,
// instead of at the call site.
const getRealSelf = () => window.self;
const getRealTop = () => window.top;

export const isEmbeddedInFrame = (getSelf = getRealSelf, getTop = getRealTop) => {
    if (process.env.NODE_ENV !== 'production') {
        return true;
    }
    try {
        return getSelf() !== getTop();
    } catch (e) {
        return true;
    }
};

export default isEmbeddedInFrame;
